/**
 * Document Queue Page
 *
 * Displays queued documents for review and processing.
 * Allows users to:
 * - View pending documents
 * - Trigger AI extraction
 * - Review extracted data
 * - Approve or reject documents
 */

import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import * as queueApi from '../api/queueApi';
import Button from '../components/common/Button';
import QueueDetailDrawer from '../components/documents/QueueDetailDrawer';
import StatusBadge from '../components/common/StatusBadge';
import ConfidenceBar from '../components/common/ConfidenceBar';
import ActionsDropdown from '../components/common/ActionsDropdown';
import SparklesIcon from '../components/common/SparklesIcon';
import { formatDate } from '../utils/formatDate';
import { getWorstSeverity } from '../utils/confidenceUtils';

/**
 * Source badge component
 */
const SourceBadge = ({ source }) => {
  return (
    <span className={`px-2 py-1 text-xs font-medium rounded ${
      source === 'api' ? 'bg-purple-100 text-purple-800' : 'bg-blue-100 text-blue-800'
    }`}>
      {source === 'api' ? 'API' : 'Web'}
    </span>
  );
};

/**
 * Document type badge component
 */
const DocTypeBadge = ({ documentType, documentSubtype }) => {
  if (documentType === 'ocean_booking') {
    const subtypeLabel = documentSubtype === 'booking_confirmation' ? 'Confirmation' : 'Request';
    return (
      <span className="px-2 py-1 text-xs font-medium rounded bg-teal-100 text-teal-800">
        OB ({subtypeLabel})
      </span>
    );
  }
  if (documentType === 'ocean_bill_of_lading') {
    return (
      <span className="px-2 py-1 text-xs font-medium rounded bg-purple-100 text-purple-800">
        B/L{documentSubtype === 'sea_waybill' ? ' (SWB)' : ''}
      </span>
    );
  }
  if (documentType === 'purchase_order') {
    return (
      <span className="px-2 py-1 text-xs font-medium rounded bg-indigo-100 text-indigo-800">
        PO
      </span>
    );
  }
  // Unclassified — document type not yet determined by the pipeline
  return (
    <span className="px-2 py-1 text-xs font-medium rounded bg-gray-100 text-gray-500">
      Pending
    </span>
  );
};

/**
 * Collect all issue messages from a document.
 *
 * New entries have a top-level `issues` array. Old entries only have
 * processingLog warnings and evaluation.flags — we fall back to those.
 */
const getDocIssues = (doc) => {
  // Prefer unified issues array when present
  if (Array.isArray(doc.issues) && doc.issues.length > 0) {
    return doc.issues.map(issue => {
      const label = (issue.type || '').replace(/_/g, ' ');
      const capitalized = label.charAt(0).toUpperCase() + label.slice(1);
      return `${capitalized}${issue.field ? ` (${issue.field})` : ''}${issue.detail ? `: ${issue.detail}` : ''}`;
    });
  }

  // Fallback for old entries
  const issues = [];

  // Pipeline warnings (duplicate checks, pricing analysis, etc.)
  if (doc.processingLog?.length > 0) {
    const logWarnings = doc.processingLog
      .filter(step => step.status === 'warning' && step.warnings?.length > 0)
      .flatMap(step => step.warnings);
    issues.push(...logWarnings);
  }

  // Evaluation flags (all types from LLM-as-judge)
  const flags = doc.evaluation?.flags || [];
  for (const flag of flags) {
    const label = flag.type.charAt(0).toUpperCase() + flag.type.slice(1);
    issues.push(`${label}${flag.field ? ` (${flag.field})` : ''}${flag.detail ? `: ${flag.detail}` : ''}`);
  }

  return issues;
};

/**
 * DocumentQueue Component
 */
function DocumentQueue() {
  const navigate = useNavigate();
  const location = useLocation();

  // Banner message from navigation state (e.g. after reprocess)
  const [bannerMessage, setBannerMessage] = useState(location.state?.message || null);

  // Auto-dismiss banner after 5 seconds
  useEffect(() => {
    if (!bannerMessage) return;
    // Clear the location state so refreshing doesn't re-show the banner
    window.history.replaceState({}, '');
    const timer = setTimeout(() => setBannerMessage(null), 5000);
    return () => clearTimeout(timer);
  }, [bannerMessage]);

  // State
  const [documents, setDocuments] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [statusFilter, setStatusFilter] = useState('pending,processing,extracted,failed');
  const [processingId, setProcessingId] = useState(null);

  // Selected document for detail panel
  const [selectedDoc, setSelectedDoc] = useState(null);
  const [loadingDetails, setLoadingDetails] = useState(false);

  /**
   * Fetch queue documents
   */
  const fetchDocuments = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await queueApi.getQueue({ status: statusFilter, limit: 100 });
      setDocuments(response.data.documents || []);
    } catch (err) {
      setError(err.message || 'Failed to load queue');
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  /**
   * Fetch queue stats
   */
  const fetchStats = async () => {
    try {
      const response = await queueApi.getQueueStats();
      setStats(response.data);
    } catch (err) {
      console.error('Failed to load stats:', err);
    }
  };

  // Load documents and stats on mount and when filter changes
  useEffect(() => {
    fetchDocuments();
    fetchStats();
  }, [fetchDocuments]);

  // Poll for updates every 5 seconds (silent — no loading spinner)
  useEffect(() => {
    const interval = setInterval(() => {
      if (processingId) return;

      queueApi.getQueue({ status: statusFilter, limit: 100 })
        .then(response => setDocuments(response.data.documents || []))
        .catch(() => {});

      queueApi.getQueueStats()
        .then(response => setStats(response.data))
        .catch(() => {});
      // Re-fetch selected document details so the timeline updates
      if (selectedDoc && ['pending', 'processing'].includes(selectedDoc.status)) {
        queueApi.getQueueEntry(selectedDoc._id)
          .then(response => setSelectedDoc(response.data))
          .catch(() => {});
      }
    }, 5000);

    return () => clearInterval(interval);
  }, [statusFilter, processingId, selectedDoc]);

  /**
   * Load document details
   */
  const handleViewDetails = async (doc) => {
    try {
      setLoadingDetails(true);
      const response = await queueApi.getQueueEntry(doc._id);
      setSelectedDoc(response.data);
    } catch (err) {
      alert(err.message || 'Failed to load document details');
    } finally {
      setLoadingDetails(false);
    }
  };

  /**
   * Process document (trigger AI extraction)
   */
  const handleProcess = async (docId) => {
    try {
      setProcessingId(docId);
      await queueApi.processDocument(docId);
      if (selectedDoc?._id === docId) {
        const detail = await queueApi.getQueueEntry(docId);
        setSelectedDoc(detail.data);
      }
      fetchDocuments();
      fetchStats();
    } catch (err) {
      alert(err.message || 'Failed to queue document for processing');
    } finally {
      setProcessingId(null);
    }
  };

  /**
   * Review document - navigate to review page
   */
  const handleReview = (doc) => {
    // Navigate to DocumentReviewer with queue entry data
    const params = new URLSearchParams({
      queueId: doc._id
    });
    navigate(`/review?${params.toString()}`);
  };

  /**
   * Reject document
   */
  const handleReject = async (docId) => {
    const notes = prompt('Enter rejection reason (optional):');
    if (notes === null) return; // User cancelled

    try {
      setProcessingId(docId);
      await queueApi.rejectDocument(docId, notes);
      setSelectedDoc(null);
      fetchDocuments();
      fetchStats();
    } catch (err) {
      alert(err.message || 'Failed to reject document');
    } finally {
      setProcessingId(null);
    }
  };

  /**
   * Delete document
   */
  const handleDelete = async (docId) => {
    if (!confirm('Permanently delete this document?')) return;

    try {
      setProcessingId(docId);
      await queueApi.deleteQueueEntry(docId);
      setSelectedDoc(null);
      fetchDocuments();
      fetchStats();
    } catch (err) {
      alert(err.message || 'Failed to delete document');
    } finally {
      setProcessingId(null);
    }
  };

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex justify-between items-start">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Document Queue</h1>
          <p className="text-gray-600">Review and process incoming documents</p>
        </div>
        <Button onClick={() => navigate('/purchase-orders/ingest')}>
          + Upload New
        </Button>
      </div>

      {/* Info banner (e.g. after reprocessing a document) */}
      {bannerMessage && (
        <div className="flex items-center justify-between bg-blue-50 border border-blue-200 text-blue-700 px-4 py-3 rounded-lg">
          <div className="flex items-center gap-2">
            <svg className="w-5 h-5 text-blue-500 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
            <span className="text-sm font-medium">{bannerMessage}</span>
          </div>
          <button onClick={() => setBannerMessage(null)} className="text-blue-400 hover:text-blue-600">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      )}

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-amber-50 border-l-4 border-l-amber-400 rounded-lg shadow-sm p-4">
            <p className="text-sm text-amber-800 font-medium">Pending</p>
            <p className="text-2xl font-bold text-amber-700">{stats.pending}</p>
            {(stats.processing || 0) > 0 && (
              <p className="text-xs text-blue-600 mt-1 font-medium">{stats.processing} processing</p>
            )}
          </div>
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
            <p className="text-sm text-gray-500">Ready for Review</p>
            <p className="text-2xl font-bold text-green-600">{stats.extracted}</p>
          </div>
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
            <p className="text-sm text-gray-500">Approved</p>
            <p className="text-2xl font-bold text-emerald-600">{stats.approved}</p>
          </div>
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
            <p className="text-sm text-gray-500">Rejected / Failed</p>
            <p className="text-2xl font-bold text-red-600">{(stats.rejected || 0) + (stats.failed || 0)}</p>
          </div>
        </div>
      )}

      {/* Filter Tabs */}
      <div className="flex items-center gap-2">
        {[
          { key: 'pending,processing,extracted,failed', label: 'Active', count: (stats?.pending || 0) + (stats?.processing || 0) + (stats?.extracted || 0) + (stats?.failed || 0) },
          { key: 'pending', label: 'Pending', count: stats?.pending || 0 },
          { key: 'extracted', label: 'Ready for Review', count: stats?.extracted || 0 },
          { key: 'approved', label: 'Approved', count: stats?.approved || 0 },
          { key: 'rejected,failed', label: 'Rejected/Failed', count: (stats?.rejected || 0) + (stats?.failed || 0) },
        ].map(tab => (
          <button
            key={tab.key}
            onClick={() => setStatusFilter(tab.key)}
            className={`px-3.5 py-1.5 rounded-full text-sm font-medium transition-colors ${
              statusFilter === tab.key
                ? 'bg-blue-600 text-white shadow-sm'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            {tab.label}
            {tab.count > 0 && (
              <span className={`ml-1.5 text-xs tabular-nums px-1.5 py-0.5 rounded-full ${
                statusFilter === tab.key
                  ? 'bg-white/20'
                  : 'bg-gray-200/80'
              }`}>
                {tab.count}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* Document List */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        {loading ? (
          <div className="p-8 text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
          </div>
        ) : error ? (
          <div className="p-8 text-center text-red-600">{error}</div>
        ) : documents.length === 0 ? (
          <div className="p-8 text-center text-gray-500">
            <svg className="w-12 h-12 mx-auto text-gray-300 mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            <p>No documents in queue</p>
          </div>
        ) : (
          <div className="divide-y divide-gray-200">
            {documents.map((doc) => {
              const issues = getDocIssues(doc);
              const canNavigate = ['extracted', 'approved', 'rejected', 'failed'].includes(doc.status);
              const worstSeverity = getWorstSeverity(doc.issues || []);
              const severityDotColor = {
                error: 'bg-red-500',
                warning: 'bg-amber-500',
                info: 'bg-blue-500',
              }[worstSeverity];
              return (
              <div
                key={doc._id}
                onClick={() => canNavigate && handleReview(doc)}
                className={`p-4 ${canNavigate ? 'cursor-pointer hover:bg-gray-50' : ''} ${
                  selectedDoc?._id === doc._id ? 'bg-blue-50' :
                  issues.length > 0 ? 'bg-amber-50 border-l-4 border-l-amber-400' : ''
                }`}
              >
                <div className="flex items-center justify-between">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <p className="font-medium text-gray-900 truncate">
                        {doc.originalFilename}
                      </p>
                      {issues.length > 0 && (
                        <span
                          className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 border border-amber-300 shrink-0"
                          title={issues.join('\n')}
                        >
                          <svg className="w-3.5 h-3.5 text-amber-500" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M8.485 2.495c.673-1.167 2.357-1.167 3.03 0l6.28 10.875c.673 1.167-.17 2.625-1.516 2.625H3.72c-1.347 0-2.189-1.458-1.515-2.625L8.485 2.495zM10 6a.75.75 0 01.75.75v3.5a.75.75 0 01-1.5 0v-3.5A.75.75 0 0110 6zm0 9a1 1 0 100-2 1 1 0 000 2z" clipRule="evenodd" />
                          </svg>
                          <span className="text-xs font-semibold">
                            {issues.length} {issues.length === 1 ? 'Issue' : 'Issues'}
                          </span>
                        </span>
                      )}
                    </div>
                    <div className="flex items-center space-x-2 mt-1">
                      <StatusBadge variant="queue" status={doc.status} />
                      <DocTypeBadge documentType={doc.documentType} documentSubtype={doc.documentSubtype} />
                      <SourceBadge source={doc.source} />
                      <span className="text-xs text-gray-500">
                        {formatDate(doc.createdAt)}
                      </span>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 ml-4">
                    {/* Compact confidence bar for docs with extraction results */}
                    {['extracted', 'approved'].includes(doc.status) && doc.confidence?.overall != null && (
                      <ConfidenceBar value={doc.confidence.overall} variant="compact" />
                    )}

                    {/* Sparkles button to open AI details drawer */}
                    {['extracted', 'approved', 'failed'].includes(doc.status) && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleViewDetails(doc);
                        }}
                        className="relative p-1.5 rounded-md text-gray-400 hover:text-blue-600 hover:bg-blue-50 transition-colors"
                        title="AI extraction details"
                      >
                        <SparklesIcon className="w-5 h-5" />
                        {severityDotColor && (
                          <span className={`absolute -top-0.5 -right-0.5 w-2 h-2 rounded-full ring-2 ring-white ${severityDotColor}`} />
                        )}
                      </button>
                    )}

                    {doc.status === 'processing' && (
                      <span className="flex items-center text-sm text-blue-600">
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600 mr-2"></div>
                        Processing
                      </span>
                    )}

                    {doc.status === 'pending' && (
                      <Button
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleProcess(doc._id);
                        }}
                        loading={processingId === doc._id}
                      >
                        Process
                      </Button>
                    )}

                    {/* Three-dot menu for extracted/rejected/failed docs */}
                    {['extracted', 'rejected', 'failed'].includes(doc.status) && (
                      <ActionsDropdown
                        isProcessing={processingId === doc._id}
                        actions={[
                          {
                            label: 'Delete',
                            variant: 'danger',
                            icon: (
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                              </svg>
                            ),
                            onClick: () => handleDelete(doc._id),
                          },
                        ]}
                      />
                    )}
                  </div>
                </div>
              </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Overlay drawer for document details */}
      <QueueDetailDrawer
        open={!!selectedDoc || loadingDetails}
        queueEntry={selectedDoc}
        evaluation={selectedDoc?.evaluation}
        confidence={selectedDoc?.confidence}
        filename={selectedDoc?.originalFilename}
        onClose={() => setSelectedDoc(null)}
        loading={loadingDetails}
      />
    </div>
  );
}

export default DocumentQueue;
